import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import moment from 'moment';
import cookie from 'js-cookie';
import config from '../../config';

import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Tabs, Tab, Menu, MenuItem, Button } from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { fetchJson } from '../Helpers/Helpers';
import NewInspectionModal from '../NewInspectionModal/NewInspectionModal';
import Lister, { ListItem, ListerInspection, ListerInstallation, ListerActions, ListerDetails } from '../Lister/Lister';
import OverviewCard from '../OverviewCard/OverviewCard';
import PlaceHolderContent from './PlaceHolderContent';

import './Overview.css';

function Overview() {
	const { t } = useSelector((redux) => redux.store);

	// Set dates and locales
	const locale = cookie.get('locale') || 'nl';
	const monday = moment().day(1);
	moment.locale(locale);

	// State
	const [inspections, setInspections] = useState(PlaceHolderContent); // PlaceHolderContent()
	const [newInspectionModalOpen, setNewInspectionModalOpen] = useState(false);
	const [overviewDate, setOverviewDate] = useState(monday);
	const [datePickerDate, setDatePickerDate] = useState(monday);
	const [datePickerOpen, setDatePickerOpen] = useState(false);
	const [weekSelectOpen, setWeekSelectOpen] = useState(false);
	const [weekSelectAnchorEl, setWeekSelectAnchorEl] = useState(null);

	// User expects to scroll up after week selection
	window.scrollTo(0, 0);

	// Register background sync event
	if ('serviceWorker' in navigator) {
		navigator.serviceWorker.ready.then(reg => reg.sync.register('outbox'));
	}

	useEffect(() => {
		getCurrentWeek();
	}, []);

	/**
	 * Hook on state change for overviewdate. Gets new data from BFF and
	 * sets the date picker date, so the date picker is in sync with the
	 * shown date on the overview
	 */
	useEffect(async () => {
		setDatePickerDate(overviewDate);
		// @todo refactor with fetchLatestInspections()
		const inspections = await fetchJson(`inspections?week=${overviewDate.format('YYYY-MM-DD')}`)
			.then(json => {
				if (json.error) {
					window.alert(t.overview_fetch_inspections_fail);
					console.error(json.error);
					return [];
					// @TODO: add to client log
				} else if (json.data) {
					return json.data;
				}
			})
			.catch(console.error);
		setInspections(inspections);
	}, [overviewDate]);

	async function getCurrentWeek() {
		if (navigator.serviceWorker &&
			navigator.serviceWorker.controller &&
			(!cookie.get('sync_week') || cookie.get('sync_week') !== monday.format('YYYY-MM-DD'))) {
			refreshOverviews();
		} else {
			setInspections(await fetchLatestInspections());
		}
	}

	function refreshOverviews() {
		const msgChan = new MessageChannel();
		msgChan.port1.onmessage = async (event) => {
			const data = JSON.parse(event.data);
			if (data.message === 'refresh-overviews') {
				if (data.error) {
					window.alert(t.overview_refresh_fail);
					console.error(data.error);
					// @TODO: add to client log
				} else {
					setInspections(await fetchLatestInspections());
				}
			}
		};
		navigator.serviceWorker.controller.postMessage('refresh-overviews', [msgChan.port2]);
	}

	function fetchLatestInspections() {
		const formattedMonday = monday.format('YYYY-MM-DD');
		return fetchJson(`inspections?week=${formattedMonday}`)
			.then(json => {
				cookie.set('sync_week', formattedMonday, { expires: 365, sameSite: 'Strict' });
				return json.data;
			});
	}

	return (
		<div className="overview-wrapper">
			<div className="week-selection">
				<Button
					variant="contained"
					disabled={ !navigator.onLine }
					color="primary"
					style={{ marginRight: '2rem' }}
					onClick={ () => setNewInspectionModalOpen(true) }
				>
					{ t.new_inspection }
				</Button>
				<Button
					variant="outlined"
					onClick={ handleWeekSelectClick }
					className="week-selection__button"
				>
					{ overviewDate.format('LL') }
					<ExpandMoreIcon />
				</Button>
				<Menu
					anchorEl={ weekSelectAnchorEl }
					open={ weekSelectOpen }
					onClose={ resetWeekSelectMenu }>
					<MenuItem
						onClick={ () => selectPresetOverviewDate(monday.subtract(1, 'week')) }
					>
						{ t.overview_prev }
					</MenuItem>
					<MenuItem
						onClick={ () => selectPresetOverviewDate(monday) }
					>
						{ t.overview_now }
					</MenuItem>
					<MenuItem
						onClick={ () => selectPresetOverviewDate(monday.add(1, 'week')) }
					>
						{ t.overview_next }
					</MenuItem>
					<MenuItem
						onClick={ handleChooseDateClick }
					>
						{ t.overview_date }
					</MenuItem>
				</Menu>
				<LocalizationProvider
					dateAdapter={ AdapterMoment }
					adapterLocale={ locale }>
					<MobileDatePicker
						renderInput={() => (
							// Not sure how to fix this. We only want the modal, not the input field itself. So make it a hidden input?
							<input
								type="hidden"
								onClick={ () => { setDatePickerOpen(!datePickerOpen); }}
							/>
						)}
						maxDate={ moment().day(1).add(3, 'months') }
						minDate={ moment().day(1).subtract(3, 'years') }
						value={ datePickerDate }
						open={ datePickerOpen }
						onClose={() => { setDatePickerOpen(false); }}
						onChange={(date) => { setDatePickerDate(date); }}
						onAccept={(date) => { setOverviewDate(moment(date).day(1)); /* always set to Monday */ }}
						cancelLabel={ t.cancel_datepicker }
					/>
				</LocalizationProvider>
			</div>
			{ inspections && inspections.map((day, index) => {
				const parsedDate = moment(day.date, 'DD-MM-YYYY');
				const isToday = parsedDate.isSame(new Date(), 'day');
				return (
					<section className={isToday ? 'overview-day overview-today' : 'overview-day'} key={index}>
						<DateHeader
							t={ t }
							unparsedDate={ day.date } />
						{ day.buildings.length > 0 && day.buildings.map((building) => {
							const address = `${building.address} ${building.zipcode}, ${building.city.toUpperCase()}`;
							const executedInspection = building.inspections.filter(inspection => inspection.executed !== null);
							return (
								<OverviewCard
									title={building.name}
									id={building.id}
									subtitle={building.city ? address : ''}
									key={building.code}
									numInspection={building.inspections.length < 1 ? 0 : building.inspections.length}
									executedInspection={executedInspection.length}
								>
									<TabSection
										building={ building }
										t={ t } />
								</OverviewCard>
							);

						}) }
						{ day.buildings.length === 0 && t.overview_card_no_inspections }
					</section>
				);
			}) }
			<NewInspectionModal
				open={ newInspectionModalOpen }
				closeHandler={() => { setNewInspectionModalOpen(false); }}
			/>
		</div>
	);

	function resetWeekSelectMenu() {
		setWeekSelectOpen(false);
		setWeekSelectAnchorEl(null);
	}

	function handleWeekSelectClick(event) {
		setWeekSelectAnchorEl(event.currentTarget);
		setWeekSelectOpen(true);
	}

	function handleChooseDateClick() {
		resetWeekSelectMenu();
		setDatePickerOpen(true);
	}

	function selectPresetOverviewDate(date) {
		resetWeekSelectMenu();
		setOverviewDate(date);
	}

}

/**
 * Renders date header for each section with proper localisation
 */
function DateHeader(props) {
	const { t, unparsedDate } = props;
	const parsedDate = moment(unparsedDate, 'DD-MM-YYYY');
	const isToday = parsedDate.isSame(new Date(), 'day');
	return (
		<h2>
			{ parsedDate.format('dddd') }
			{ isToday && `(${t.today})` }
			<span>
				{ parsedDate.format('LL') /* see Moment.js docs: preferred date formatting for selected locale */ }
			</span>
		</h2>
	);
}

/**
 * Render tabbed section (inspections & installations)
 */
function TabSection(props) {
	const { t, building } = props;
	const tabIndex = {
		INSPECTIONS: 0,
		INSTALLATIONS: 1,
	};
	const [selectedTab, setSelectedTab] = useState(tabIndex.INSPECTIONS);

	const inspections = building.inspections;
	const installations = building.installations;
	const sortedInspections = inspections.sort((a, b) => (a.id >= b.id) ? 1 : -1);

	return (
		<section>
			<Tabs
				centered
				variant="fullWidth"
				value={ selectedTab }
				indicatorColor="primary"
				textColor="primary"
				onChange={ (_, value) => setSelectedTab(value) }
			>
				<Tab
					label={ t.overview_card_inspections }
				/>
				<Tab
					label={ t.overview_card_installations }
				/>
			</Tabs>
			{ selectedTab === tabIndex.INSPECTIONS && (
				<Lister>
					{ sortedInspections && sortedInspections.map((inspection) => (
						<Inspection
							t={ t }
							key={ inspection.id }
							inspection={ inspection }
						/>
					))}
				</Lister>
			)}
			{ selectedTab === tabIndex.INSTALLATIONS && (
				<Lister>
					{ installations && installations.map((installation) => (
						<Installation
							t={ t }
							key={ installation.id }
							installation={ installation }
						/>
					))}
				</Lister>
			)}
		</section>
	);
}

/**
 * Renders inspection details and controls in the Inspections tab of a section
 */
function Inspection(props) {
	const { t, inspection } = props;
	const label = `${inspection.indication} (${inspection[`${config.app_short_brand_name}_number`]})`;
	const history = useHistory();

	return (
		<ListItem id={inspection.id}>
			<ListerInspection
				t={ t }
				label={ label }
				address={ inspection.placement_address }
				installation={ `${inspection.installation_number || t.unknown_installation } (${inspection.supplier})` }
				inspectionType={ inspection.inspection_type }
			/>
			<ListerActions id={inspection.id}
				t={ t }
				history={ history /* @FIXME */ }
				title={ inspection[`${config.app_short_brand_name}_number`] }
				done={ inspection.executed }
			/>
		</ListItem>
	);
}

/**
 * Renders installation details in the Installations tab of a section
 */
function Installation(props) {
	const { t, installation } = props;
	const label = `${installation[`${config.app_short_brand_name}_number`]} (${installation.indication})`;

	return (
		<ListItem>
			<ListerInstallation
				t={ t }
				label={ label }
				subtitle={ `${installation.placement_address}, ${installation.zip} ${installation.city}` }
			/>
			<ListerDetails
				t={ t }
				label={ installation.supplier }
				installation={ installation.installation_number }
				notified_body_number={ installation.notified_body_number }
			/>
		</ListItem>
	);
}

export default Overview;
